import React, { useState, useRef, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../../utils/apiClient';
import ExcelJS from 'exceljs';
import { parseExcelWithWorker, batchData } from '../../../utils/excelParserWorker';
import ConflictResolutionModal from './ConflictResolutionModal';
import { REQUIRED_COLUMNS, KNOWN_COLUMNS } from '../../../config/columnMappings';

const UploadMatrixModal = React.memo(({ isOpen, onClose, onUploadSuccess, onUploadError, projectId }) => {
  const { instance, accounts } = useMsal();
  const [dragActive, setDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState('');
  const [parsedData, setParsedData] = useState(null);
  const [columnMatches, setColumnMatches] = useState(null);
  const [validationError, setValidationError] = useState('');
  const [step, setStep] = useState('select'); // 'select', 'validate', 'confirm', 'mapColumns', 'uploading'
  const fileInputRef = useRef(null);
  const [unmappedColumns, setUnmappedColumns] = useState([]);
  const [udColumnMappings, setUdColumnMappings] = useState({});
  const [hasUuidColumn, setHasUuidColumn] = useState(false);
  const [existingColumnMappings, setExistingColumnMappings] = useState([]);
  const [parseProgress, setParseProgress] = useState({ message: '', percent: 0 });
  const [uploadBatchInfo, setUploadBatchInfo] = useState({ current: 0, total: 0 });
  const [useWorker, setUseWorker] = useState(true); // Toggle for Web Worker parsing
  const [conflicts, setConflicts] = useState([]);
  const [showConflictModal, setShowConflictModal] = useState(false);
  const [conflictResolution, setConflictResolution] = useState(null);

  // Fetch existing column mappings for this project when modal opens
  React.useEffect(() => {
    const fetchExistingMappings = async () => {
      if (isOpen && projectId && accounts && accounts.length > 0) {
        try {
          const mappingsResponse = await authenticatedApiCall(
            instance,
            accounts,
            'GET',
            `/matrix/project/${projectId}/column-mappings`
          );
          
          if (mappingsResponse.data.success) {
            setExistingColumnMappings(mappingsResponse.data.data || []);
            console.log('Existing column mappings loaded:', mappingsResponse.data.data);
          }
        } catch (error) {
          console.error('Error fetching existing column mappings:', error);
          setExistingColumnMappings([]);
        }
      }
    };
    
    fetchExistingMappings();
  }, [isOpen, projectId, instance, accounts]);

  // Column mappings imported from shared config: src/config/columnMappings.js
  // REQUIRED_COLUMNS and KNOWN_COLUMNS are now imported at the top

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || 
          file.type === 'application/vnd.ms-excel' ||
          file.name.endsWith('.xlsx') || 
          file.name.endsWith('.xls')) {
        setSelectedFile(file);
        parseExcelFile(file);
      } else {
        onUploadError('Please select an Excel file (.xlsx or .xls)');
      }
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || 
          file.type === 'application/vnd.ms-excel' ||
          file.name.endsWith('.xlsx') || 
          file.name.endsWith('.xls')) {
        setSelectedFile(file);
        parseExcelFile(file);
      } else {
        onUploadError('Please select an Excel file (.xlsx or .xls)');
      }
    }
  };

  // Progress callback for Web Worker parsing
  const handleParseProgress = useCallback((message, percent) => {
    setParseProgress({ message, percent });
  }, []);

  const parseExcelFile = async (file) => {
    setLoading(true);
    setValidationError('');
    setStep('validate');
    setParseProgress({ message: 'Starting...', percent: 0 });
    
    try {
      console.log('Starting Excel file parse...');
      console.log('File details:', {
        name: file.name,
        type: file.type,
        size: file.size,
        lastModified: new Date(file.lastModified).toISOString()
      });
      
      // Use Web Worker for large files (> 500KB) to prevent UI lockup
      const useWorkerForParsing = useWorker && file.size > 500 * 1024;
      console.log('Using Web Worker:', useWorkerForParsing);
      
      let headers, dataRows, matches, hasUuid, extraColumns, autoMappedCustomColumns, unmatchedRequired;
      
      if (useWorkerForParsing) {
        // Parse using Web Worker (off main thread)
        console.log('Parsing with Web Worker...');
        const result = await parseExcelWithWorker(file, existingColumnMappings, handleParseProgress);
        
        headers = result.headers;
        dataRows = result.dataRows;
        matches = result.matches;
        hasUuid = result.hasUuid;
        extraColumns = result.extraColumns;
        autoMappedCustomColumns = result.autoMappedCustomColumns;
        unmatchedRequired = result.unmatchedRequired;
        
        console.log('Worker parsing complete:', result.rowCount, 'rows');
      } else {
        // Parse on main thread (for smaller files)
        console.log('Parsing on main thread...');
        const buffer = await file.arrayBuffer();
        
        if (buffer.byteLength === 0) {
          throw new Error('File is empty (0 bytes)');
        }
        
        const uint8Array = new Uint8Array(buffer);
        if (uint8Array[0] !== 0x50 || uint8Array[1] !== 0x4B) {
          throw new Error('File does not appear to be a valid Excel .xlsx file');
        }
        
        const workbook = new ExcelJS.Workbook();
        await workbook.xlsx.load(buffer);
        
        if (!workbook.worksheets || workbook.worksheets.length === 0) {
          throw new Error('No worksheets found in the Excel file');
        }
        
        const worksheet = workbook.worksheets.find(
          ws => ws && ws.name && ws.name.toLowerCase() === 'import'
        );
        
        if (!worksheet) {
          const availableSheets = workbook.worksheets
            .filter(ws => ws && ws.name)
            .map(ws => ws.name)
            .join(', ');
          throw new Error(`Sheet named 'Import' not found. Available sheets: ${availableSheets || 'none'}`);
        }
        
        // Get headers
        const headerRow = worksheet.getRow(1);
        headers = [];
        headerRow.eachCell((cell, colNumber) => {
          headers[colNumber - 1] = cell.value ? String(cell.value).trim() : '';
        });
        
        const maxCol = headerRow.cellCount;
        for (let i = 0; i < maxCol; i++) {
          if (headers[i] === undefined) headers[i] = '';
        }
        
        // Get data rows
        dataRows = [];
        worksheet.eachRow((row, rowNumber) => {
          if (rowNumber === 1) return;
          
          const rowData = [];
          row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
            const cellValue = cell.value;
            if (cellValue === null || cellValue === undefined) {
              rowData[colNumber - 1] = null;
            } else if (typeof cellValue === 'object' && cellValue.result !== undefined) {
              rowData[colNumber - 1] = cellValue.result !== null ? String(cellValue.result) : null;
            } else {
              rowData[colNumber - 1] = String(cellValue);
            }
          });
          
          for (let i = 0; i < headers.length; i++) {
            if (rowData[i] === undefined) rowData[i] = null;
          }
          
          if (rowData.some(cell => cell !== null && cell !== '')) {
            dataRows.push(rowData);
          }
        });
        
        // Check UUID
        const uuidIndex = headers.findIndex(h => h && h.trim().toLowerCase() === 'uuid');
        hasUuid = uuidIndex !== -1;
        
        // Match columns
        matches = {};
        const matchedIndices = new Set();
        
        if (hasUuid) {
          matches['UUID'] = { found: true, excelColumn: headers[uuidIndex], dbColumn: 'uuid', index: uuidIndex };
          matchedIndices.add(uuidIndex);
        }
        
        Object.keys(KNOWN_COLUMNS).forEach(knownCol => {
          const foundIndex = headers.findIndex(h => h && h.trim().toLowerCase() === knownCol.toLowerCase());
          if (foundIndex !== -1) {
            const dbColumn = KNOWN_COLUMNS[knownCol];
            const alreadyMatched = Object.values(matches).some(m => m.found && m.dbColumn === dbColumn);
            if (!alreadyMatched) {
              matches[knownCol] = { found: true, excelColumn: headers[foundIndex], dbColumn, index: foundIndex };
              matchedIndices.add(foundIndex);
            } else {
              matchedIndices.add(foundIndex);
            }
          }
        });
        
        unmatchedRequired = [];
        if (!hasUuid) {
          Object.keys(REQUIRED_COLUMNS).forEach(requiredCol => {
            const dbColumn = REQUIRED_COLUMNS[requiredCol];
            const isMatched = Object.values(matches).some(m => m.found && m.dbColumn === dbColumn);
            if (!isMatched) unmatchedRequired.push(requiredCol);
          });
        }
        
        extraColumns = [];
        autoMappedCustomColumns = {};
        
        headers.forEach((header, index) => {
          if (header && !matchedIndices.has(index)) {
            const existingMapping = existingColumnMappings.find(
              m => m.custom_name && m.custom_name.toLowerCase() === header.toLowerCase()
            );
            
            if (existingMapping) {
              matches[header] = {
                found: true,
                excelColumn: header,
                dbColumn: existingMapping.column_key,
                index: index,
                isCustomColumn: true
              };
              matchedIndices.add(index);
              autoMappedCustomColumns[header] = {
                targetColumn: existingMapping.column_key,
                customName: existingMapping.custom_name,
                isEditable: existingMapping.is_editable
              };
            } else {
              extraColumns.push({ excelHeader: header, index });
            }
          }
        });
      }
      
      console.log('Data rows found:', dataRows.length);
      
      if (dataRows.length === 0) {
        throw new Error('Excel file must have at least one data row');
      }
      
      setHasUuidColumn(hasUuid);
      setColumnMatches(matches);
      
      if (unmatchedRequired && unmatchedRequired.length > 0) {
        setValidationError(`Missing required columns: ${unmatchedRequired.join(', ')}. Note: Line Number is only required for fresh imports (no UUID column) or for new rows in re-imports (empty UUID).`);
        setLoading(false);
        return;
      }
      
      setUnmappedColumns(extraColumns);
      
      if (Object.keys(autoMappedCustomColumns).length > 0) {
        setUdColumnMappings(autoMappedCustomColumns);
      }
      
      const rawData = { headers, dataRows, matches };
      
      if (extraColumns.length > 0) {
        setParsedData(rawData);
        setStep('mapColumns');
        setLoading(false);
      } else {
        const transformedData = dataRows
          .filter(row => row && row.some(cell => cell !== null && cell !== ''))
          .map(row => {
            const obj = {};
            Object.keys(matches).forEach(requiredCol => {
              const match = matches[requiredCol];
              if (match.found) {
                const value = row[match.index];
                obj[match.dbColumn] = value !== null && value !== '' ? value : null;
              }
            });
            return obj;
          });
        
        console.log('Data transformed, rows:', transformedData.length);
        setParsedData(transformedData);
        setStep('confirm');
        setLoading(false);
      }
      
    } catch (error) {
      console.error('Error parsing Excel file:', error);
      setValidationError(error.message);
      setStep('select');
      setLoading(false);
      setSelectedFile(null);
      onUploadError(`Failed to parse Excel file: ${error.message}`);
    }
  };

  const handleBrowseClick = () => {
    fileInputRef.current.click();
  };

  const handleColumnMappingComplete = () => {
    // Transform data with UD column mappings
    const { headers, dataRows, matches } = parsedData;
    
    const transformedData = dataRows
      .filter(row => row && row.some(cell => cell !== null && cell !== ''))
      .map(row => {
        const obj = {};
        
        // Add standard matched columns
        Object.keys(matches).forEach(requiredCol => {
          const match = matches[requiredCol];
          if (match.found) {
            const value = row[match.index];
            obj[match.dbColumn] = value !== null && value !== '' ? value : null;
          }
        });
        
        // Add UD column mappings
        Object.keys(udColumnMappings).forEach(excelHeader => {
          const mapping = udColumnMappings[excelHeader];
          if (mapping.targetColumn && mapping.targetColumn !== 'skip') {
            const unmappedCol = unmappedColumns.find(col => col.excelHeader === excelHeader);
            if (unmappedCol) {
              const value = row[unmappedCol.index];
              obj[mapping.targetColumn] = value !== null && value !== '' ? value : null;
            }
          }
        });
        
        return obj;
      });
    
    setParsedData(transformedData);
    setStep('confirm');
  };

  // Check for conflicts before upload (called when user clicks "Confirm & Upload")
  const checkForConflicts = async () => {
    console.log("🔍 checkForConflicts CALLED - parsedData:", parsedData?.length);
    console.log("🔍 parsedData sample:", parsedData?.slice(0, 2));
    console.log("🔍 parsedData keys:", parsedData?.[0] ? Object.keys(parsedData[0]) : 'no data');
    if (!parsedData || parsedData.length === 0) {
      onUploadError('No data to upload');
      return;
    }
    
    setLoading(true);
    setUploadStatus('Checking for duplicates...');
    
    try {
      if (!accounts || accounts.length === 0) {
        throw new Error('No authenticated user found');
      }
      
      // Extract iso_sheet_number values ONLY from rows WITHOUT UUIDs
      // Rows WITH UUIDs will either update (if UUID exists) or insert as new (if UUID doesn't exist)
      // Only rows WITHOUT UUIDs can cause duplicates if their ISO already exists
      const rowsWithoutUuid = parsedData.filter(row => !row.uuid || String(row.uuid).trim() === '');
      const rowsWithUuid = parsedData.filter(row => row.uuid && String(row.uuid).trim() !== '');
      
      const isoSheetNumbers = rowsWithoutUuid
        .map(row => row.iso_sheet_number)
        .filter(iso => iso && String(iso).trim() !== '');
      
      // Also get unique ISOs from rows with UUIDs (for logging)
      const isosWithUuid = rowsWithUuid
        .map(row => row.iso_sheet_number)
        .filter(iso => iso && String(iso).trim() !== '');
      
      console.log("🔍 Rows without UUID (need conflict check):", rowsWithoutUuid.length);
      console.log("🔍 Rows with UUID (will update/insert by UUID):", rowsWithUuid.length);
      console.log("🔍 ISOs to check for conflicts:", isoSheetNumbers.length, isoSheetNumbers.slice(0, 5));
      console.log("🔍 ISOs that will update by UUID:", isosWithUuid.length, isosWithUuid.slice(0, 5));
      
      // Skip conflict check if no iso_sheet_numbers from rows without UUIDs
      if (isoSheetNumbers.length === 0) {
        console.log('No iso_sheet_number values from rows without UUIDs, proceeding with upload');
        proceedWithUpload(null);
        return;
      }
      
      // Call conflict check API
      // Only checking ISOs from rows without UUIDs - these are the only ones that can cause duplicates
      const conflictResponse = await authenticatedApiCall(
        instance,
        accounts,
        'POST',
        '/matrix/check-conflicts',
        {
          projectId: projectId,
          isoSheetNumbers: isoSheetNumbers
          // Not sending UUIDs - we've already filtered to only check rows without UUIDs
        }
      );
      
      console.log("🔍 Conflict response:", conflictResponse.data);
      if (conflictResponse.data.success && conflictResponse.data.data.conflictCount > 0) {
        // Conflicts found - show resolution modal
        setConflicts(conflictResponse.data.data.conflicts);
        setShowConflictModal(true);
        setLoading(false);
      } else {
        // No conflicts - proceed with upload
        proceedWithUpload(null);
      }
      
    } catch (error) {
      console.error('Error checking for conflicts:', error);
      setLoading(false);
      // On error, show user and don't proceed - this prevents silent duplicates
      onUploadError(`Failed to check for duplicates: ${error.message}. Please try again.`);
    }
  };
  
  // Handle conflict resolution from modal
  const handleConflictResolution = (resolution) => {
    setShowConflictModal(false);
    setConflictResolution(resolution);
    proceedWithUpload(resolution);
  };
  
  // Close conflict modal and cancel upload
  const handleConflictCancel = () => {
    setShowConflictModal(false);
    setConflicts([]);
    setLoading(false);
  };
  
  // Proceed with actual upload (called after conflict check/resolution)
  const proceedWithUpload = async (resolution) => {
    setStep('uploading');
    setLoading(true);
    setUploadProgress(0);
    setUploadStatus('Preparing upload...');
    
    try {
      if (!accounts || accounts.length === 0) {
        throw new Error('No authenticated user found');
      }

      // Prepare column mappings for backend
      const columnMappingsArray = Object.keys(udColumnMappings)
        .map(excelHeader => {
          const mapping = udColumnMappings[excelHeader];
          if (mapping.targetColumn && mapping.targetColumn !== 'skip') {
            return {
              column_key: mapping.targetColumn,
              custom_name: mapping.customName || excelHeader,
              is_editable: mapping.isEditable !== undefined ? mapping.isEditable : true,
              original_excel_header: excelHeader
            };
          }
          return null;
        })
        .filter(m => m !== null);

      // Determine batch size based on data size
      // For large datasets (>2000 rows), use batched uploads to prevent timeouts
      const BATCH_SIZE = 2000;
      const useBatchedUpload = parsedData.length > BATCH_SIZE;
      
      if (useBatchedUpload) {
        // Batched upload for large datasets
        const batches = batchData(parsedData, BATCH_SIZE);
        const totalBatches = batches.length;
        let totalRowsQueued = 0;
        let allJobIds = [];
        const grandTotalRows = parsedData.length;  // Track total rows across all batches
        
        console.log(`Starting batched upload: ${grandTotalRows} rows in ${totalBatches} batches`);
        setUploadBatchInfo({ current: 0, total: totalBatches });
        
        for (let i = 0; i < batches.length; i++) {
          const batch = batches[i];
          const batchNum = i + 1;
          const rowsUploadedSoFar = i * BATCH_SIZE;
          const rowsAfterThisBatch = Math.min(rowsUploadedSoFar + batch.length, grandTotalRows);
          
          setUploadBatchInfo({ current: batchNum, total: totalBatches });
          setUploadStatus(`Uploading batch ${batchNum} of ${totalBatches} (${rowsAfterThisBatch.toLocaleString()} of ${grandTotalRows.toLocaleString()} rows)...`);
          setUploadProgress(Math.floor((i / totalBatches) * 90) + 5);
          
          // Send batch to backend
          const uploadResponse = await authenticatedApiCall(
            instance,
            accounts,
            'POST',
            '/matrix/upload-parsed',
            {
              projectId: projectId,
              data: batch,
              columnMappings: i === 0 ? columnMappingsArray : [], // Only send mappings with first batch
              isReimport: hasUuidColumn,
              batchInfo: {
                batchNumber: batchNum,
                totalBatches: totalBatches,
                isFirstBatch: i === 0,
                isLastBatch: i === totalBatches - 1,
                grandTotalRows: grandTotalRows  // Pass total rows across all batches
              },
              conflictResolution: i === 0 ? resolution : null // Only send resolution with first batch
            }
          );
          
          if (uploadResponse.data.success) {
            totalRowsQueued += uploadResponse.data.data.rowsQueued || batch.length;
            if (uploadResponse.data.data.jobId) {
              allJobIds.push(uploadResponse.data.data.jobId);
            }
          } else {
            throw new Error(`Batch ${batchNum} failed: ${uploadResponse.data.message || 'Unknown error'}`);
          }
          
          // Small delay between batches to prevent overwhelming the server
          if (i < batches.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 100));
          }
        }
        
        setUploadProgress(100);
        setUploadStatus('Upload complete!');
        
        setTimeout(() => {
          // Pass the last job ID for monitoring (it will complete last)
          const lastJobId = allJobIds[allJobIds.length - 1];
          onUploadSuccess({
            message: `Successfully queued ${grandTotalRows.toLocaleString()} rows for processing`,
            jobId: lastJobId,
            totalJobs: allJobIds.length,
            allJobIds: allJobIds,
            totalRows: grandTotalRows  // Pass the grand total for accurate display
          });
          resetModal();
        }, 1000);
        
      } else {
        // Single upload for smaller datasets
        setUploadProgress(10);
        setUploadStatus('Uploading data to server...');
        
        const uploadResponse = await authenticatedApiCall(
          instance,
          accounts,
          'POST',
          '/matrix/upload-parsed',
          {
            projectId: projectId,
            data: parsedData,
            columnMappings: columnMappingsArray,
            isReimport: hasUuidColumn,
            conflictResolution: resolution
          }
        );

        setUploadProgress(100);
        setUploadStatus('Upload complete!');

        if (uploadResponse.data.success) {
          setTimeout(() => {
            onUploadSuccess({
              message: `Successfully queued ${uploadResponse.data.data.rowsQueued} rows for processing`,
              jobId: uploadResponse.data.data.jobId,
              totalJobs: 1,
              allJobIds: [uploadResponse.data.data.jobId]
            });
            resetModal();
          }, 1000);
        } else {
          throw new Error(uploadResponse.data.message || 'Upload failed');
        }
      }
    } catch (error) {
      console.error('Error uploading data:', error);
      setUploadStatus('Upload failed');
      onUploadError(error.response?.data?.message || error.message || 'Failed to upload data');
      setStep('confirm');
    } finally {
      setLoading(false);
      setUploadBatchInfo({ current: 0, total: 0 });
    }
  };

  const resetModal = () => {
    setSelectedFile(null);
    setParsedData(null);
    setColumnMatches(null);
    setValidationError('');
    setStep('select');
    setUploadProgress(0);
    setUploadStatus('');
    setDragActive(false);
    setUnmappedColumns([]);
    setUdColumnMappings({});
    setHasUuidColumn(false);
    setExistingColumnMappings([]);
    setParseProgress({ message: '', percent: 0 });
    setUploadBatchInfo({ current: 0, total: 0 });
    setConflicts([]);
    setShowConflictModal(false);
    setConflictResolution(null);
    onClose();
  };

  const handleBack = () => {
    if (step === 'confirm' && unmappedColumns.length > 0) {
      setStep('mapColumns');
    } else {
      setSelectedFile(null);
      setParsedData(null);
      setColumnMatches(null);
      setValidationError('');
      setUnmappedColumns([]);
      setUdColumnMappings({});
      setHasUuidColumn(false);
      setExistingColumnMappings([]);
      setStep('select');
    }
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '2rem',
        width: '90%',
        maxWidth: (step === 'confirm' || step === 'mapColumns') ? '800px' : '500px',
        maxHeight: '90vh',
        overflowY: 'auto',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem'
        }}>
          <h3 style={{ margin: 0, color: '#333' }}>
            Upload Matrix
            {step === 'validate' && ' - Validating...'}
            {step === 'mapColumns' && ' - Map Extra Columns'}
            {step === 'confirm' && ' - Review & Confirm'}
            {step === 'uploading' && ' - Uploading...'}
          </h3>
          <button
            onClick={resetModal}
            disabled={loading}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.5rem',
              cursor: loading ? 'not-allowed' : 'pointer',
              color: '#666',
              padding: '0.25rem',
              opacity: loading ? 0.5 : 1
            }}
          >
            ×
          </button>
        </div>

        {/* Step 1: File Selection */}
        {step === 'select' && (
          <>
            <div
              style={{
                border: `2px dashed ${dragActive ? '#007bff' : '#ddd'}`,
                borderRadius: '8px',
                padding: '2rem',
                textAlign: 'center',
                backgroundColor: dragActive ? '#f8f9ff' : '#f8f9fa',
                transition: 'all 0.2s ease',
                marginBottom: '1.5rem'
              }}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📊</div>
              
              {selectedFile ? (
                <div>
                  <p style={{ margin: '0 0 1rem 0', color: '#28a745', fontWeight: 'bold' }}>
                    File selected: {selectedFile.name}
                  </p>
                  <p style={{ margin: 0, color: '#666', fontSize: '0.9rem' }}>
                    Size: {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
              ) : (
                <div>
                  <p style={{ margin: '0 0 1rem 0', color: '#333', fontSize: '1.1rem' }}>
                    {dragActive ? 'Drop your Excel file here' : 'Drag and drop your Excel file here'}
                  </p>
                  <p style={{ margin: '0 0 1rem 0', color: '#666' }}>
                    or
                  </p>
                  <button
                    onClick={handleBrowseClick}
                    style={{
                      padding: '0.75rem 1.5rem',
                      backgroundColor: '#007bff',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '1rem'
                    }}
                  >
                    Browse Files
                  </button>
                  <p style={{ margin: '1rem 0 0 0', color: '#666', fontSize: '0.9rem' }}>
                    Supports .xlsx and .xls files
                  </p>
                </div>
              )}
            </div>

            <div style={{
              backgroundColor: '#e3f2fd',
              padding: '1rem',
              borderRadius: '4px',
              fontSize: '0.9rem',
              color: '#1976d2'
            }}>
              <strong>Requirements:</strong>
              <ul style={{ margin: '0.5rem 0 0 0', paddingLeft: '1.5rem' }}>
                <li>Excel file must contain a sheet named <strong>"Import"</strong></li>
                <li><strong>Fresh Import:</strong> Requires <strong>Line Number</strong> column (other columns are optional)</li>
                <li><strong>Re-Import/Update:</strong> Include <strong>UUID</strong> column to update existing rows. Only the columns present in your file will be updated.</li>
                <li>Rows with empty or missing UUID values will be inserted as new rows (Line Number required)</li>
                <li>Exported files include a table named <strong>"ImportTable"</strong> with UUID column for easy re-imports</li>
                <li><strong>Deletion:</strong> Type "d" in the <strong>Action</strong> column to delete rows during re-import (UUID required). ⚠️ <em>Warning: This also deletes progress history!</em></li>
              </ul>
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
          </>
        )}

        {/* Step 2: Validation in progress */}
        {step === 'validate' && (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            {/* Show validation error if present */}
            {validationError ? (
              <>
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>❌</div>
                <div style={{
                  backgroundColor: '#ffebee',
                  padding: '1.5rem',
                  borderRadius: '8px',
                  marginBottom: '1.5rem',
                  textAlign: 'left'
                }}>
                  <p style={{
                    margin: '0 0 1rem 0',
                    fontWeight: 'bold',
                    color: '#c62828',
                    fontSize: '1.1rem'
                  }}>
                    ⚠️ Validation Failed
                  </p>
                  <p style={{
                    margin: 0,
                    color: '#b71c1c',
                    lineHeight: '1.5'
                  }}>
                    {validationError}
                  </p>
                </div>
                <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '1rem' }}>
                  Please ensure your Excel file contains the required columns and try again.
                </p>
                <button
                  onClick={() => {
                    setStep('select');
                    setSelectedFile(null);
                    setValidationError('');
                    setParseProgress({ message: '', percent: 0 });
                  }}
                  style={{
                    padding: '0.75rem 1.5rem',
                    fontSize: '1rem',
                    backgroundColor: '#007bff',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  ← Select Different File
                </button>
              </>
            ) : (
              <>
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⏳</div>
                <p style={{ color: '#666', marginBottom: '1rem' }}>
                  {parseProgress.message || 'Validating Excel file structure...'}
                </p>
                {parseProgress.percent > 0 && (
                  <div style={{ marginTop: '1rem' }}>
                    <div style={{
                      width: '100%',
                      height: '8px',
                      backgroundColor: '#e9ecef',
                      borderRadius: '4px',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        width: `${parseProgress.percent}%`,
                        height: '100%',
                        backgroundColor: '#007bff',
                        transition: 'width 0.3s ease'
                      }} />
                    </div>
                    <p style={{ color: '#666', fontSize: '0.85rem', marginTop: '0.5rem' }}>
                      {parseProgress.percent}% complete
                    </p>
                  </div>
                )}
                {selectedFile && selectedFile.size > 500 * 1024 && (
                  <p style={{ color: '#17a2b8', fontSize: '0.85rem', marginTop: '1rem' }}>
                    🔄 Processing large file in background thread...
                  </p>
                )}
              </>
            )}
          </div>
        )}

        {/* Step 2.5: Column Mapping for Extra Columns */}
        {step === 'mapColumns' && (
          <>
            {hasUuidColumn && (
              <div style={{
                backgroundColor: '#e3f2fd',
                padding: '1rem',
                borderRadius: '4px',
                marginBottom: '1.5rem',
                fontSize: '0.9rem',
                color: '#1976d2'
              }}>
                <strong>🔄 Re-import Mode Detected:</strong> UUID column found. Rows with valid UUIDs will be updated (only columns present in your file). Rows with empty/missing UUIDs will be inserted as new (Line Number required).
              </div>
            )}

            <div style={{
              backgroundColor: '#fff3cd',
              padding: '1rem',
              borderRadius: '4px',
              marginBottom: '1.5rem',
              fontSize: '0.9rem',
              color: '#856404'
            }}>
              <strong>Extra Columns Detected:</strong> {unmappedColumns.length} column(s) found that are not part of the standard matrix template. 
              Map them to User-Defined columns (UD1-UD10), provide a display name, and choose if they should be editable.
            </div>

            {validationError && (
              <div style={{
                backgroundColor: '#ffebee',
                padding: '1rem',
                borderRadius: '4px',
                marginBottom: '1.5rem',
                fontSize: '0.9rem',
                color: '#c62828'
              }}>
                <strong>⚠️ Validation Error:</strong> {validationError}
              </div>
            )}

            <div style={{
              maxHeight: '400px',
              overflowY: 'auto',
              border: '1px solid #ddd',
              borderRadius: '4px',
              padding: '1rem',
              marginBottom: '1.5rem'
            }}>
              <h4 style={{ marginTop: 0, marginBottom: '1rem', color: '#333' }}>
                Map Extra Columns
              </h4>
              
              {unmappedColumns.map((col, index) => {
                const mapping = udColumnMappings[col.excelHeader] || { targetColumn: '', isEditable: true };
                const usedUdColumns = Object.values(udColumnMappings)
                  .filter(m => m.targetColumn && m.targetColumn !== 'skip' && m.targetColumn !== mapping.targetColumn)
                  .map(m => m.targetColumn);
                
                return (
                  <div key={index} style={{
                    padding: '1rem',
                    marginBottom: '1rem',
                    border: '1px solid #e0e0e0',
                    borderRadius: '4px',
                    backgroundColor: '#f8f9fa'
                  }}>
                    <div style={{ fontWeight: 'bold', marginBottom: '0.5rem', color: '#495057' }}>
                      Excel Column: {col.excelHeader}
                    </div>
                    
                    <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                      <div style={{ flex: 1, minWidth: '200px' }}>
                        <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.85rem', color: '#666' }}>
                          Map to Database Column:
                        </label>
                        <select
                          value={mapping.targetColumn}
                          onChange={(e) => {
                            setUdColumnMappings(prev => ({
                              ...prev,
                              [col.excelHeader]: {
                                ...prev[col.excelHeader],
                                targetColumn: e.target.value,
                                // Pre-fill custom name with Excel header when first mapping
                                customName: prev[col.excelHeader]?.customName || col.excelHeader
                              }
                            }));
                          }}
                          style={{
                            width: '100%',
                            padding: '0.5rem',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            fontSize: '0.9rem'
                          }}
                        >
                          <option value="">-- Select --</option>
                          <option value="skip">Skip this column</option>
                          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => {
                            const udfKey = `udf${String(num).padStart(2, '0')}`;
                            const isUsed = usedUdColumns.includes(udfKey);
                            return (
                              <option key={udfKey} value={udfKey} disabled={isUsed}>
                                UDF{String(num).padStart(2, '0')} {isUsed ? '(already mapped)' : ''}
                              </option>
                            );
                          })}
                        </select>
                      </div>
                      
                      {mapping.targetColumn && mapping.targetColumn !== 'skip' && (
                        <>
                          <div style={{ flex: 1, minWidth: '200px' }}>
                            <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.85rem', color: '#666' }}>
                              Display Name (Frontend):
                            </label>
                            <input
                              type="text"
                              value={mapping.customName || col.excelHeader}
                              onChange={(e) => {
                                setUdColumnMappings(prev => ({
                                  ...prev,
                                  [col.excelHeader]: {
                                    ...prev[col.excelHeader],
                                    customName: e.target.value
                                  }
                                }));
                              }}
                              placeholder="Enter display name..."
                              style={{
                                width: '100%',
                                padding: '0.5rem',
                                border: '1px solid #ddd',
                                borderRadius: '4px',
                                fontSize: '0.9rem'
                              }}
                            />
                          </div>
                          
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', paddingTop: '1.5rem' }}>
                            <label style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.5rem',
                              cursor: 'pointer',
                              fontSize: '0.9rem',
                              whiteSpace: 'nowrap'
                            }}>
                              <input
                                type="checkbox"
                                checked={mapping.isEditable !== undefined ? mapping.isEditable : true}
                                onChange={(e) => {
                                  setUdColumnMappings(prev => ({
                                    ...prev,
                                    [col.excelHeader]: {
                                      ...prev[col.excelHeader],
                                      isEditable: e.target.checked
                                    }
                                  }));
                                }}
                                style={{ cursor: 'pointer' }}
                              />
                              Editable
                            </label>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <div style={{
              display: 'flex',
              gap: '1rem',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={handleBack}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '1rem'
                }}
              >
                Back
              </button>
              <button
                onClick={() => {
                  // Validate that all mapped columns have custom names
                  const missingNames = [];
                  Object.keys(udColumnMappings).forEach(excelHeader => {
                    const mapping = udColumnMappings[excelHeader];
                    if (mapping.targetColumn && mapping.targetColumn !== 'skip') {
                      if (!mapping.customName || mapping.customName.trim() === '') {
                        missingNames.push(excelHeader);
                      }
                    }
                  });
                  
                  if (missingNames.length > 0) {
                    setValidationError(`Please provide display names for: ${missingNames.join(', ')}`);
                    return;
                  }
                  
                  handleColumnMappingComplete();
                }}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '1rem'
                }}
              >
                Continue to Review
              </button>
            </div>
          </>
        )}

        {/* Step 3: Column validation results and confirmation */}
        {step === 'confirm' && columnMatches && (
          <>
            {hasUuidColumn && (
              <div style={{
                backgroundColor: '#e3f2fd',
                padding: '1rem',
                borderRadius: '4px',
                marginBottom: '1rem',
                fontSize: '0.9rem',
                color: '#1976d2'
              }}>
                <strong>🔄 Re-import Mode:</strong> UUID column detected. Rows with valid UUIDs will be updated (only columns present in your file). Rows with empty/missing UUIDs will be inserted as new (Line Number required).
                <br />
                <span style={{ color: '#dc3545' }}>💡 <strong>Deletion:</strong> To delete rows during import, type "d" in the "Action" column for rows you want to delete.</span>
                <br />
                <span style={{ color: '#dc3545' }}>⚠️ <strong>Warning:</strong> Deleting rows also permanently removes their progress history (shop trends). Use with caution!</span>
              </div>
            )}
            
            <div style={{
              backgroundColor: validationError ? '#ffebee' : '#e8f5e9',
              padding: '1rem',
              borderRadius: '4px',
              marginBottom: '1.5rem'
            }}>
              <p style={{
                margin: 0,
                fontWeight: 'bold',
                color: validationError ? '#c62828' : '#2e7d32'
              }}>
                {validationError || `✓ All columns matched! Found ${parsedData?.length || 0} data rows`}
              </p>
              {/* Show delete count if any rows have action='d' */}
              {!validationError && parsedData && parsedData.filter(r => r.action && r.action.toLowerCase() === 'd').length > 0 && (
                <div style={{ marginTop: '0.5rem', padding: '0.5rem', backgroundColor: '#ffe6e6', borderRadius: '4px', border: '1px solid #dc3545' }}>
                  <p style={{ margin: 0, color: '#dc3545', fontWeight: 'bold' }}>
                    🗑️ Rows marked for deletion: {parsedData.filter(r => r.action && r.action.toLowerCase() === 'd').length}
                  </p>
                  <p style={{ margin: '0.25rem 0 0 0', color: '#721c24', fontSize: '0.85rem' }}>
                    ⚠️ <strong>Warning:</strong> Deleting rows will also permanently remove all their progress history (shop progress trends). This cannot be undone.
                  </p>
                </div>
              )}
            </div>

            {!validationError && (
              <>
                <div style={{
                  marginBottom: '1.5rem',
                  maxHeight: '400px',
                  overflowY: 'auto',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  padding: '1rem'
                }}>
                  <h4 style={{ marginTop: 0, marginBottom: '1rem', color: '#333' }}>
                    Column Validation Results
                  </h4>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
                    gap: '0.5rem'
                  }}>
                    {Object.keys(columnMatches).map((col) => {
                      const match = columnMatches[col];
                      return (
                        <div
                          key={col}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: '0.5rem',
                            backgroundColor: match.found ? '#e8f5e9' : '#ffebee',
                            borderRadius: '4px',
                            fontSize: '0.85rem'
                          }}
                        >
                          <span style={{
                            marginRight: '0.5rem',
                            fontSize: '1rem'
                          }}>
                            {match.found ? '✓' : '✗'}
                          </span>
                          <span style={{
                            color: match.found ? '#2e7d32' : '#c62828',
                            wordBreak: 'break-word'
                          }}>
                            {col}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {Object.keys(udColumnMappings).filter(k => udColumnMappings[k].targetColumn && udColumnMappings[k].targetColumn !== 'skip').length > 0 && (
                  <div style={{
                    backgroundColor: '#e3f2fd',
                    padding: '1rem',
                    borderRadius: '4px',
                    marginBottom: '1.5rem',
                    fontSize: '0.9rem',
                    color: '#1976d2'
                  }}>
                    <strong>📋 Custom Column Mappings:</strong>
                    <ul style={{ margin: '0.5rem 0 0 0', paddingLeft: '1.5rem' }}>
                      {Object.keys(udColumnMappings).map(excelHeader => {
                        const mapping = udColumnMappings[excelHeader];
                        if (mapping.targetColumn && mapping.targetColumn !== 'skip') {
                          // Check if this was auto-mapped (exists in existingColumnMappings)
                          const wasAutoMapped = existingColumnMappings.some(
                            existing => existing.column_key === mapping.targetColumn && 
                                      existing.custom_name.toLowerCase() === excelHeader.toLowerCase()
                          );
                          return (
                            <li key={excelHeader}>
                              <strong>{mapping.customName || excelHeader}</strong> → {mapping.targetColumn.toUpperCase()} 
                              {mapping.isEditable === false && ' (Read-only)'}
                              {wasAutoMapped && ' ✓ (Auto-detected from existing column)'}
                            </li>
                          );
                        }
                        return null;
                      }).filter(Boolean)}
                    </ul>
                  </div>
                )}
                
                <div style={{
                  backgroundColor: '#fff3cd',
                  padding: '1rem',
                  borderRadius: '4px',
                  marginBottom: '1.5rem',
                  fontSize: '0.9rem',
                  color: '#856404'
                }}>
                  <strong>⚠️ Important:</strong> Please review the column matches above. Once confirmed, {parsedData?.length || 0} rows will be uploaded to the database.
                  {Object.keys(udColumnMappings).filter(k => udColumnMappings[k].targetColumn && udColumnMappings[k].targetColumn !== 'skip').length > 0 && 
                    ' Custom column mappings will be saved and the columns will appear in your project view.'}
                </div>
              </>
            )}

            <div style={{
              display: 'flex',
              gap: '1rem',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={handleBack}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '1rem'
                }}
              >
                Back
              </button>
              {!validationError && (
                <button
                  onClick={checkForConflicts}
                  style={{
                    padding: '0.75rem 1.5rem',
                    backgroundColor: '#28a745',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '1rem'
                  }}
                >
                  Confirm & Upload {parsedData?.length || 0} Rows
                </button>
              )}
            </div>
          </>
        )}

        {/* Step 4: Upload Progress */}
        {step === 'uploading' && (
          <>
            <div style={{ marginBottom: '1.5rem' }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '0.5rem'
              }}>
                <span style={{ fontSize: '0.9rem', color: '#666' }}>{uploadStatus}</span>
                <span style={{ fontSize: '0.9rem', fontWeight: 'bold' }}>{uploadProgress}%</span>
              </div>
              <div style={{
                width: '100%',
                height: '8px',
                backgroundColor: '#e9ecef',
                borderRadius: '4px',
                overflow: 'hidden'
              }}>
                <div style={{
                  width: `${uploadProgress}%`,
                  height: '100%',
                  backgroundColor: uploadProgress === 100 ? '#28a745' : '#007bff',
                  transition: 'width 0.3s ease'
                }} />
              </div>
              {uploadBatchInfo.total > 1 && (
                <div style={{ marginTop: '1rem' }}>
                  <p style={{ 
                    color: '#17a2b8', 
                    fontSize: '0.85rem', 
                    margin: 0,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}>
                    <span>📦</span>
                    <span>
                      Batch {uploadBatchInfo.current} of {uploadBatchInfo.total}
                      {uploadBatchInfo.current < uploadBatchInfo.total && ' - Processing in batches to ensure reliability'}
                    </span>
                  </p>
                </div>
              )}
            </div>
          </>
        )}
      </div>
      
      {/* Conflict Resolution Modal */}
      <ConflictResolutionModal
        isOpen={showConflictModal}
        onClose={handleConflictCancel}
        conflicts={conflicts}
        onResolve={handleConflictResolution}
      />
    </div>
  );
});

export default UploadMatrixModal;
